import { createContext, useContext, useState, useCallback } from 'react';
import { getCurrentUser, clearAuth, getRefreshToken, type SiteUser } from '../lib/auth';
import { apiPost } from '../lib/api';

interface AuthState {
  user: SiteUser | null;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  isAuthenticated: false,
  logout: async () => {},
  refreshAuth: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SiteUser | null>(() => getCurrentUser());

  const refreshAuth = useCallback(() => {
    const customer = getCurrentUser();
    setUser(customer);
    if (customer?.sub && typeof window.MAP === 'function') {
      window.MAP('identify', customer.sub);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const rt = getRefreshToken();
      if (rt) await apiPost('/customer/auth/logout', { refreshToken: rt }, { skipAuth: true });
    } catch {
      /* ignore */
    }
    clearAuth();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, logout, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
